import type { ObjectCategory } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, PlusCircle } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeleteObjectCategory, useObjectCategories } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { SearchField } from '@/shared/components/inputs';
import { PageHeading } from '@/shared/components/layout';
import { DataTablePagination, SortableHeader, TableLoadingCell } from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { getSortParam } from '@/shared/utils/table-utils';

const ActionCell = ({
  category,
  onDelete,
  isGmnAdmin,
}: {
  category: ObjectCategory;
  onDelete: (category: ObjectCategory) => void;
  isGmnAdmin: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="size-8 p-0">
          <span className="sr-only">{t('navigation.openMenu')}</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => navigate(`/gmn/settings/object-categories/${category.id}`)}
        >
          {t('common.edit')}
        </DropdownMenuItem>
        {isGmnAdmin && (
          <DropdownMenuItem onClick={() => onDelete(category)}>
            {t('common.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function ObjectCategoriesOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = currentUser?.role === 'gmn-admin';

  const tablePreferences = useTablePreferences({
    tableId: 'object-categories-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  const [categoryToDelete, setCategoryToDelete] = useState<ObjectCategory | null>(null);
  const deleteCategory = useDeleteObjectCategory();

  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleDelete = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory.mutateAsync(categoryToDelete.id);
        toast.success(t('objectCategories.categoryDeletedSuccessfully'));
        setCategoryToDelete(null);
      } catch (_error) {
        toast.error(t('objectCategories.errorDeletingCategory'));
      }
    }
  };

  const columns: ColumnDef<ObjectCategory>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => <div className="truncate font-medium">{row.getValue('name')}</div>,
      size: 250,
      minSize: 150,
      maxSize: 400,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <SortableHeader column={column} title={t('common.description')} />,
      cell: ({ row }) => (
        <div className="truncate text-muted-foreground">{row.getValue('description') || '-'}</div>
      ),
      size: 400,
      minSize: 200,
      maxSize: 600,
    },
    {
      id: 'metadataFields',
      header: t('objectCategories.metadataFields'),
      cell: ({ row }) => {
        const schema = row.original.metadataSchema as {
          properties?: Record<string, unknown>;
        } | null;
        const fieldCount = schema?.properties ? Object.keys(schema.properties).length : 0;
        return <div>{fieldCount}</div>;
      },
      size: 120,
      minSize: 80,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return <div>{date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}</div>;
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <ActionCell
          category={row.original}
          onDelete={setCategoryToDelete}
          isGmnAdmin={isGmnAdmin}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: categoriesData, isLoading } = useObjectCategories({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where: debouncedSearchTerm ? { name: { like: debouncedSearchTerm } } : undefined,
    sort: getSortParam<ObjectCategory>(sorting),
  });

  const categories = categoriesData?.docs || [];
  const totalPages = categoriesData?.totalPages || 0;

  const table = useReactTable({
    data: categories,
    columns,
    pageCount: totalPages,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <>
      <PageHeading>{t('objectCategories.objectCategories')}</PageHeading>

      <div className="flex items-center">
        <SearchField searchTerm={searchTerm} setSearchTerm={handleSearchChange} />
        <div className="ml-auto">
          <Button
            onClick={() => navigate('/gmn/settings/object-categories/new')}
            disabled={!isGmnAdmin}
          >
            <PlusCircle className="size-4 mr-2" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
              {t('objectCategories.addCategory')}
            </span>
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="w-full overflow-auto">
          <Table className="w-full table-fixed">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="group">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} style={{ width: `${header.getSize()}px` }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !isLoading ? (
                <EmptyTableRow colSpan={columns.length} />
              ) : (
                <TableLoadingCell colSpan={columns.length} />
              )}
            </TableBody>
          </Table>
        </div>

        <DataTablePagination table={table} />
      </div>

      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('objectCategories.deleteCategory')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('objectCategories.deleteCategoryConfirmation', {
                name: categoryToDelete?.name || t('objectCategories.category'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteCategory.isPending}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
